/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * A screen's or an action's detail (contract section 5, "tap -> detail"): GET /v1/admin/usage/features/:kind/:name with the
 * lens, in a drawer over the ranked list: the daily series (events, users, engagement), the top params and, for a screen,
 * what came before and after it.
 */
import { api } from '../../lib/api';
import { KIND_LABELS, durationText, nameLabel, normalizeFeatureDetail, type FeatureRow, type UsageLens, type UsageQuery } from '../../lib/usage';
import { Drawer, EmptyState, Sparkline, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { NamedBars, Trend, UsageError } from './parts';

export function FeatureDrawer({ row, lens, query, onClose }: { row: FeatureRow | null; lens: UsageLens; query: UsageQuery; onClose: () => void }) {
  if (!row) return null;
  return <OpenFeatureDrawer row={row} lens={lens} query={query} onClose={onClose} />;
}

function OpenFeatureDrawer({ row, lens, query, onClose }: { row: FeatureRow; lens: UsageLens; query: UsageQuery; onClose: () => void }) {
  const key = `detail|${row.kindRaw}|${row.name}|${JSON.stringify(query)}`;
  const { load, reload, reloading } = useLoad(() => api.usageFeature(row.kindRaw, row.name, query).then((wire) => normalizeFeatureDetail(wire, { name: row.name, kind: row.kind })), key);
  const detail = load.state === 'ready' ? load.data : null;
  const words = KIND_LABELS[row.kind];
  const paramColumns: Array<Column<{ key: string; value: string; count: number }>> = [
    { key: 'key', header: 'Param', cell: (param) => <Tag>{param.key}</Tag> },
    { key: 'value', header: 'Value', mono: true, cell: (param) => param.value || '—' },
    { key: 'count', header: 'Events', numeric: true, cell: (param) => count(param.count) },
  ];

  return (
    <Drawer open onClose={onClose} title={nameLabel(row.name)} description={`${words.one} · ${count(row.events)} events · ${count(row.users)} users · last ${lens.window} days`}>
      {load.state === 'failed' ? <UsageError title="The detail could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /> : (
        <div className="ui-stack">
          <section className="ui-section" aria-label="Per day">
            <h3 className="ui-section-title">Per day</h3>
            {load.state === 'loading' ? <EmptyState compact title="Loading" /> : detail && detail.days.length ? (
              <>
                <Trend values={detail.days.map((day) => day.events)} days={detail.days.map((day) => day.day)} label="Events per day" />
                <dl className="ui-facts">
                  <dt>Users per day</dt><dd><Sparkline values={detail.days.map((day) => day.users)} label="Users per day" /></dd>
                  <dt>Engagement per day</dt><dd><Sparkline values={detail.days.map((day) => day.engagementMs)} label="Engagement per day" tone="accent" /> <span className="ui-cell-sub">{durationText(detail.totals.engagementMs)} in all</span></dd>
                </dl>
              </>
            ) : <EmptyState compact title="No days yet">The series fills in as days are collected.</EmptyState>}
          </section>
          <section className="ui-section" aria-label="Top params">
            <h3 className="ui-section-title">Top params</h3>
            {load.state === 'loading' ? null : (
              <Table label="Top params" columns={paramColumns} rows={detail?.params ?? []} rowKey={(param) => `${param.key}=${param.value}`} stickyHeader={false} empty={<EmptyState compact title="No params">{`This ${words.one.toLowerCase()} carries no params in this window.`}</EmptyState>} />
            )}
          </section>
          {row.kind === 'screen' ? (
            <>
              <section className="ui-section" aria-label="Came from">
                <h3 className="ui-section-title">Came from</h3>
                {load.state === 'loading' ? null : <NamedBars rows={detail?.previous ?? []} label="Previous screen" empty={<EmptyState compact title="No previous screen">People opened this screen first, or the window holds no transitions yet.</EmptyState>} />}
              </section>
              <section className="ui-section" aria-label="Went to">
                <h3 className="ui-section-title">Went to</h3>
                {load.state === 'loading' ? null : <NamedBars rows={detail?.next ?? []} label="Next screen" empty={<EmptyState compact title="No next screen">People left after this screen, or the window holds no transitions yet.</EmptyState>} />}
              </section>
            </>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}
